// Which entries the application's schema would refuse, and where.
//
// Continue runs the whole form through zod, and on this screen a failure has
// nowhere to show: the list is cards, not fields, so "check the highlighted
// fields" highlighted nothing. schemaAgreement.test keeps the sheet and the
// schema in step, but when they drift anyway the driver still has to be told
// which entry is the problem — so the list marks it and Continue names it.
//
// Rows are checked one at a time and matched by the row itself, never by
// position, because the payload drops blank placeholders and positions shift.

import { schema } from '../../utils/form'

import { isBlankExperience } from './formBridge'
import type { FormExperience } from './formBridge'

export interface SchemaProblem {
  experience: FormExperience
  /** The field zod named, as a driver would call it. */
  field: string
  message: string
}

const FIELD_LABELS: Record<string, string> = {
  companyName: 'company name',
  position: 'job title',
  reasonForLeaving: 'reason for leaving',
  unemploymentReason: 'reason',
  startDate: 'start date',
  endDate: 'end date',
  dotNumber: 'DOT number',
  email: 'email',
}

const rowSchema = schema.shape.employmentExperiences.element

export const schemaProblems = (
  experiences: FormExperience[]
): SchemaProblem[] =>
  experiences
    .filter((experience) => !isBlankExperience(experience))
    .flatMap((experience) => {
      const result = rowSchema.safeParse(experience)
      if (result.success) return []
      const [issue] = result.error.issues
      const key = String(issue.path[0] ?? '')
      return [
        {
          experience,
          field: FIELD_LABELS[key] ?? key,
          message: issue.message,
        },
      ]
    })

/** One sentence naming the first entry to fix and what is wrong with it. */
export const describeSchemaProblem = (problem: SchemaProblem): string => {
  const name = problem.experience.isUnemployment
    ? 'A period of unemployment'
    : problem.experience.companyName?.trim() || 'An employer'
  return `${name} needs attention: ${
    problem.field ? `${problem.field} — ` : ''
  }${problem.message}. Tap it to fix.`
}
